import * as RadixTabs from '@radix-ui/react-tabs';
import { cn } from '@/lib/cn';

/*
  Tab set.

  Radix gives the WAI-ARIA tabs pattern: role=tablist, arrow key navigation between tabs with
  Home and End, one tab stop for the whole list so Tab moves from the list into the panel
  rather than through every tab, and aria-controls / aria-labelledby wired both ways.

  activationMode is "automatic", the default: arrowing onto a tab selects it. That is correct
  here because every panel is already loaded from the same request, so selection costs
  nothing. If a panel ever gets its own fetch this should become "manual", otherwise arrowing
  past three tabs fires three requests.
*/

export interface TabDefinition {
  readonly value: string;
  readonly label: string;
  /** Optional count or status shown after the label, for example a session count. */
  readonly badge?: React.ReactNode;
}

export interface TabsProps {
  readonly tabs: readonly TabDefinition[];
  readonly value: string;
  readonly onValueChange: (value: string) => void;
  /** Accessible name for the tab list. Say what the tabs are of. */
  readonly label: string;
  readonly children: React.ReactNode;
  readonly className?: string;
}

export function Tabs({
  tabs,
  value,
  onValueChange,
  label,
  children,
  className,
}: TabsProps): React.JSX.Element {
  return (
    <RadixTabs.Root value={value} onValueChange={onValueChange} className={className}>
      <RadixTabs.List
        aria-label={label}
        className="flex flex-wrap items-center gap-1 border-b border-border px-2"
      >
        {tabs.map((tab) => (
          <RadixTabs.Trigger
            key={tab.value}
            value={tab.value}
            className={cn(
              'relative -mb-px inline-flex items-center gap-1.5 rounded-t-[var(--radius-control)]',
              'border-b-2 border-transparent px-3 py-2 text-sm font-medium text-fg-muted',
              'transition-colors hover:text-fg',
              'data-[state=active]:border-accent data-[state=active]:text-fg',
            )}
          >
            {tab.label}
            {tab.badge === undefined ? null : (
              <span className="rounded-full bg-surface-sunken px-1.5 text-[11px] font-semibold text-fg-subtle tabular-nums">
                {tab.badge}
              </span>
            )}
          </RadixTabs.Trigger>
        ))}
      </RadixTabs.List>
      {children}
    </RadixTabs.Root>
  );
}

/**
 * A panel. tabIndex 0 so a keyboard user can scroll it and so focus lands somewhere
 * meaningful after Tab leaves the tab list; outline-none because the focus ring on a whole
 * panel is noise and the panel is not an activatable control.
 */
export function TabPanel({
  value,
  children,
  className,
}: {
  readonly value: string;
  readonly children: React.ReactNode;
  readonly className?: string;
}): React.JSX.Element {
  return (
    <RadixTabs.Content value={value} tabIndex={0} className={cn('p-4 outline-none', className)}>
      {children}
    </RadixTabs.Content>
  );
}
